import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import { useBiometricAuth } from "@/hooks/useBiometricAuth";

interface BiometricLockContextType {
  isLocked: boolean;
  unlock: () => void;
  lock: () => void;
}

const BiometricLockContext = createContext<BiometricLockContextType>({
  isLocked: false,
  unlock: () => {},
  lock: () => {},
});

export const useBiometricLock = () => useContext(BiometricLockContext);

interface BiometricLockProviderProps {
  children: ReactNode;
}

const LOCK_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export function BiometricLockProvider({ children }: BiometricLockProviderProps) {
  const { isAvailable, isEnabled, isLoading } = useBiometricAuth();
  const [isLocked, setIsLocked] = useState(false);
  const [lastActiveTime, setLastActiveTime] = useState(Date.now());

  // Lock on app startup if biometric is enabled
  useEffect(() => {
    if (!isLoading && isAvailable && isEnabled && Capacitor.isNativePlatform()) {
      setIsLocked(true);
    }
  }, [isLoading, isAvailable, isEnabled]);

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      setLastActiveTime(Date.now());
    };

    window.addEventListener("touchstart", updateActivity);
    window.addEventListener("click", updateActivity);
    window.addEventListener("keydown", updateActivity);

    return () => {
      window.removeEventListener("touchstart", updateActivity);
      window.removeEventListener("click", updateActivity);
      window.removeEventListener("keydown", updateActivity);
    };
  }, []);

  // Lock when app goes to background (for native apps)
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !isEnabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // App went to background - note the time
        setLastActiveTime(Date.now());
      } else if (document.visibilityState === "visible") {
        // App came back - check if we should lock
        const elapsed = Date.now() - lastActiveTime;
        if (elapsed > LOCK_TIMEOUT_MS && isEnabled) {
          setIsLocked(true);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isEnabled, lastActiveTime]);

  const unlock = useCallback(() => {
    setIsLocked(false);
    setLastActiveTime(Date.now());
  }, []);

  const lock = useCallback(() => {
    if (isAvailable && isEnabled) {
      setIsLocked(true);
    }
  }, [isAvailable, isEnabled]);

  return (
    <BiometricLockContext.Provider value={{ isLocked, unlock, lock }}>
      {children}
    </BiometricLockContext.Provider>
  );
}
